<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import _isEqual from 'lodash/isEqual';

import { defCurPage, defPageSize } from '@/constants/routeQuery';

export interface Props {
  modelValue?: number;
  items?: number[];
  // Per-panel pagers (e.g. AddressBookTable) keep page state locally instead of
  // in the route — `local` emits update:modelValue rather than pushing the query.
  local?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: defPageSize,
  items: () => [10, 25, 50, 100, 200],
  local: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

const filedValue = ref<number>(props.modelValue);

const router = useRouter();
const route = useRoute();

function handleUpdateModelValue(value: number): void {
  if (props.local) {
    emit('update:modelValue', value);
    return;
  }
  const query = { ...route.query, page: defCurPage, page_size: value };
  const isReplace = _isEqual(route.query, query);
  void router.push({
    query,
    force: true,
    replace: isReplace,
  });
}

watch(
  () => props.modelValue,
  (newModelValue) => {
    filedValue.value = newModelValue;
  },
  { immediate: true },
);
</script>

<template>
  <v-select
    v-model="filedValue"
    :class="$style.paginationSelectField"
    :items="props.items"
    variant="outlined"
    density="compact"
    data-testid="paginationSelectField"
    @update:model-value="handleUpdateModelValue"
  >
  </v-select>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.paginationSelectField {
  width: 72px;

  :global {
    .v-field {
      font-size: 0.875rem;
      padding-right: 8px;
    }
    .v-field__input {
      min-height: 32px;
      padding-top: 5px;
      padding-bottom: 4px;
      padding-left: 14px;
    }
    .v-field__outline > div {
      opacity: 1;
      border-color: colors.$color-gray-300;
    }
    .v-field__outline__start {
      border-radius: 4px 0 0 4px;
    }
    .v-field__outline__end {
      border-radius: 0 4px 4px 0;
    }
  }
}
</style>
